export interface PollingOptions {
  interval?: number // Polling interval in milliseconds (default: 2000)
  timeout?: number // Maximum timeout in milliseconds (default: 30000)
  maxAttempts?: number // Maximum number of polling attempts (default: 15)
}

export interface PollingResult<T> {
  success: boolean
  data?: T
  error?: string
  attempts: number
  duration: number
}

/**
 * Utility function for polling until a condition is met or timeout occurs
 * @param validator Function that returns the expected data when condition is met, null otherwise
 * @param options Polling configuration options
 * @returns Promise with polling result
 */
export async function pollForCondition<T>(
  validator: () => Promise<T | null>,
  options: PollingOptions = {}
): Promise<PollingResult<T>> {
  const {
    interval = 2000, // 2 seconds
    timeout = 30000, // 30 seconds
    maxAttempts = Math.floor(timeout / interval)
  } = options

  const startTime = Date.now()
  let attempts = 0

  while (attempts < maxAttempts) {
    attempts++
    
    try {
      const result = await validator()
      
      if (result !== null) {
        return {
          success: true,
          data: result,
          attempts,
          duration: Date.now() - startTime
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Polling attempt ${attempts} failed:`, error)
      }
    }

    // Check if we've exceeded the timeout
    if (Date.now() - startTime >= timeout) {
      break
    }

    // Wait for the next interval
    await new Promise(resolve => setTimeout(resolve, interval))
  }

  return {
    success: false,
    error: `Polling failed after ${attempts} attempts and ${Date.now() - startTime}ms`,
    attempts,
    duration: Date.now() - startTime
  }
}
